'use client';

import { useEffect, useState, useCallback } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { hapticFeedback } from '@/lib/platform';

type Tab = 'today' | 'daily-sync' | 'tools' | 'learn' | 'profile';

const TABS: { id: Tab; label: string; href: string; icon: (active: boolean) => React.ReactNode }[] = [
  {
    id: 'today',
    label: 'Today',
    href: '/',
    icon: (active) => (
      <svg className="w-6 h-6" fill={active ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={active ? 0 : 1.5}
          d={active
            ? 'M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25'
            : 'm2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25'}
        />
      </svg>
    ),
  },
  {
    id: 'daily-sync',
    label: 'Daily Sync',
    href: '/daily-sync',
    icon: (active) => (
      <svg className="w-6 h-6" fill={active ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={active ? 0 : 1.5}
          d="M9.348 14.652a3.75 3.75 0 010-5.304m5.304 0a3.75 3.75 0 010 5.304m-7.425 2.121a6.75 6.75 0 010-9.546m9.546 0a6.75 6.75 0 010 9.546M5.106 18.894c-3.808-3.808-3.808-9.98 0-13.788m13.788 0c3.808 3.808 3.808 9.98 0 13.788M12 12h.008v.008H12V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
        />
      </svg>
    ),
  },
  {
    id: 'tools',
    label: 'Tools',
    href: '/tools',
    icon: (active) => (
      <svg className="w-6 h-6" fill={active ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={active ? 0 : 1.5}
          d="M11.42 15.17l-5.1-5.1a2.25 2.25 0 113.18-3.18l5.1 5.1m-7.07 7.07l-1.5 1.5a2.25 2.25 0 01-3.18-3.18l1.5-1.5m10.6-10.6l1.5-1.5a2.25 2.25 0 013.18 3.18l-1.5 1.5m-7.07 7.07l5.1 5.1a2.25 2.25 0 003.18-3.18l-5.1-5.1"
        />
      </svg>
    ),
  },
  {
    id: 'learn',
    label: 'Learn',
    href: '/insights',
    icon: (active) => (
      <svg className="w-6 h-6" fill={active ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={active ? 0 : 1.5}
          d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
        />
      </svg>
    ),
  },
  {
    id: 'profile',
    label: 'Profile',
    href: '/dashboard',
    icon: (active) => (
      <svg className="w-6 h-6" fill={active ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={active ? 0 : 1.5}
          d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
        />
      </svg>
    ),
  },
];

function getActiveTab(pathname: string): Tab {
  if (pathname === '/') return 'today';
  if (pathname.startsWith('/daily-sync')) return 'daily-sync';
  if (pathname.startsWith('/tools') || pathname.startsWith('/calculators') || pathname.startsWith('/splitcause') || pathname.startsWith('/tech-radar')) return 'tools';
  if (pathname.startsWith('/dashboard') || pathname.startsWith('/command-center') || pathname.startsWith('/settings')) return 'profile';
  // Everything else falls under "Learn"
  return 'learn';
}

export default function NativeTabBar() {
  const pathname = usePathname();
  const [native, setNative] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cap = (window as any).Capacitor;
    if (cap?.isNativePlatform?.()) {
      setNative(true);
    }
  }, []);

  const handleTabTap = useCallback(() => {
    hapticFeedback('light');
  }, []);

  if (!native) return null;

  const activeTab = getActiveTab(pathname);

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-[9990] bg-background border-t border-border"
      style={{ paddingBottom: 'var(--safe-area-bottom, 0px)' }}
      aria-label="Main navigation"
    >
      <div className="flex items-center justify-around h-14" role="tablist">
        {TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <Link
              key={tab.id}
              href={tab.href}
              role="tab"
              aria-label={tab.label}
              aria-selected={isActive}
              aria-current={isActive ? 'page' : undefined}
              onClick={handleTabTap}
              className={`flex flex-col items-center justify-center gap-0.5 w-full h-full transition-colors ${
                isActive ? 'text-accent' : 'text-foreground-tertiary'
              }`}
            >
              {tab.icon(isActive)}
              <span className="text-[10px] font-medium">{tab.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
